/**
 * Fonctions utilitaires diverses
 */

/**
 * Formate un montant en centimes en devise
 * @param {number} cents - Montant en centimes
 * @param {string} [currency='EUR'] - Code devise
 * @returns {string}
 */
export function formatPrice(cents, currency = 'EUR') {
  const amount = cents / 100;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Formate une date
 * @param {Date|string} date
 * @param {string} [format='short'] - 'short', 'long', 'time'
 * @returns {string}
 */
export function formatDate(date, format = 'short') {
  const d = new Date(date);

  if (format === 'time') {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }

  if (format === 'long') {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
  }

  return new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Débounce une fonction
 * @param {Function} fn
 * @param {number} delay - Délai en ms
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Génère un ID unique
 * @returns {string}
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Met en majuscule la première lettre d'une chaîne tout en conservant le reste intact.
 * @param {string} value
 * @returns {string}
 */
export function capitalizeFirstLetter(value) {
  if (typeof value !== 'string') return value;
  if (value.length === 0) return value;

  const leadingWhitespaceMatch = value.match(/^\s*/);
  const leadingWhitespace = leadingWhitespaceMatch ? leadingWhitespaceMatch[0] : '';
  const trimmedStart = value.slice(leadingWhitespace.length);

  if (!trimmedStart) {
    return leadingWhitespace;
  }

  const firstCodePoint = trimmedStart.codePointAt(0);
  const firstChar = firstCodePoint !== undefined ? String.fromCodePoint(firstCodePoint) : '';
  const rest = trimmedStart.slice(firstChar.length);

  return `${leadingWhitespace}${firstChar.toUpperCase()}${rest}`;
}

/**
 * Vérifie si une valeur est vide
 * @param {any} value
 * @returns {boolean}
 */
export function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Échappe les caractères HTML
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Attend un délai
 * @param {number} ms - Millisecondes
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Clone profond d'un objet
 * @param {any} obj
 * @returns {any}
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Détermine si le message d'erreur est lié au délai de sécurité d'authentification
 * @param {string} message
 * @returns {boolean}
 */
export function isAuthRateLimitError(message) {
  if (!message) return false;
  return message.toLowerCase().includes('for security purposes, you can only request this after');
}

/**
 * Traduit le message d'erreur de délai de sécurité d'authentification en français
 * @param {string} message
 * @returns {string}
 */
export function translateAuthRateLimitError(message) {
  if (!message) return message;
  
  // Format attendu : "For security purposes, you can only request this after 13 seconds."
  const match = message.match(/after (\d+) seconds/i);
  if (match && match[1]) {
    return `Par mesure de sécurité, vous ne pouvez refaire cette demande que dans ${match[1]} secondes.`;
  }
  
  return "Par mesure de sécurité, veuillez patienter quelques instants avant de réessayer.";
}

/**
 * Formate un numéro de téléphone (Français)
 * @param {string} phone 
 * @returns {string}
 */
export function formatPhone(phone) {
  if (!phone) return '';
  // Supprimer tout ce qui n'est pas un chiffre
  const cleaned = String(phone).replace(/\D/g, '');
  
  let result = cleaned;
  // Si format international +33, convertir en 0X
  if (cleaned.startsWith('33') && (cleaned.length === 11 || cleaned.length === 12)) {
    // Si 11 chiffres (336...), c'est bon. Si 12 (3306...), on enlève le 0.
    if (cleaned.length === 12 && cleaned[2] === '0') {
      result = '0' + cleaned.substring(3);
    } else {
      result = '0' + cleaned.substring(2);
    }
  }
  
  // Si on a 10 chiffres commençant par 0, on formate
  if (result.length === 10 && result.startsWith('0')) {
    return result.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }
  
  return phone; // Retourner l'original si on ne reconnaît pas le format
}
